import { Subject, debounceTime } from 'rxjs';
import { CoordinatorService, MANIFEST_STORE_DELAY } from './coordinator.service';
import { XItem } from './x-item';
import { ScriptDataset } from './script-dataset';

type DP = {
    id?: string;
    isPreview?: boolean;
};

export class Dataset {
    isReady = false;
    isPreview = false;

    shortIdCounter = 360;

    private _markForManifestStoreSubject$ = new Subject<void>();
    private _markForManifestStore$ = this._markForManifestStoreSubject$.pipe(
        debounceTime(MANIFEST_STORE_DELAY),
    );

    private _xitemMap = new Map<string, XItem>();
    private _xitemMainMap = new Map<string, XItem>();
    private _keys = new Set<string>();

    // private _previewXItem: XItem | undefined

    private _processDuplicatesRequest$ = new Subject<void>();

    private _sortMeta?: { type: string; isDescending: boolean };
    private _sortedMainXitemLs?: XItem[];

    constructor(
        private coords: CoordinatorService,
        params: DP,
    ) {
        this.isPreview = !!params.isPreview;
        this._processDuplicatesRequest$.pipe(debounceTime(200)).subscribe(() => {
            // console.log("%c PROCESS DUPLICATES REQUEST FOR ",
            //     this.coords.consoleEm("navy", "red"),
            //     this.isPreview ? "PREVIEW" : "MAIN")
            // this.coords.project$.consoleMark
            this._processDuplicates();
        });

        this._markForManifestStore$.subscribe(() => this._manifestStore());
    }

    existingIdSet(s: Set<string>) {
        const newS = new Set<string>();
        s.forEach((id) => {
            if (this._xitemMainMap.has(id)) newS.add(id);
            else {
                const itm = this._xitemMap.get(id);
                if (itm) newS.add(itm.parentXitem!.id);
            }
        });

        return newS;
    }

    markForManifestStore() {
        this._markForManifestStoreSubject$.next();
    }

    requestXitemUpdate(xitm: XItem) {
        xitm.refreshKeys();
        this.requestReduplicate();
    }

    requestReduplicate() {
        // console.log("%c REQUEST REDUPLICATE FOR ",
        //     this.coords.consoleEm("blue", "orange"), this.isPreview)
        // console.trace()
        this._processDuplicatesRequest$.next();
    }

    has(itmId: string, includeDuplicates = false) {
        if (includeDuplicates) {
            return this._xitemMap.has(itmId);
        }
        return this._xitemMainMap.has(itmId);
    }

    get keys() {
        if (!this._keys.size) {
            this.mainXitemLs.forEach((xitm) => xitm.keys?.forEach((k) => this._keys.add(k)));
        }
        return this._keys;
    }

    hasId(itmId: string) {
        return this._xitemMap.has(itmId);
    }

    getMainIdfromAnyId(anyId: string) {
        const xitm = this._xitemMap.get(anyId);
        if (!xitm) return undefined;
        return xitm.parentXitem?.id || xitm.id;
    }

    refreshKeys() {
        this._xitemMap.forEach((xitm) => xitm.refreshKeys());
        this.requestReduplicate();
    }

    get inOtherDatasetCount() {
        const s = (this.isPreview ? this.coords.dataset$.main : this.coords.dataset$.preview)
            .xitemIdSet;
        let c = 0;
        this._xitemMap.forEach((xitm) => s.has(xitm.id) && c++);
        return c;
    }

    get id() {
        if (!this.coords.project$) return '';
        const idTag = this.isPreview ? 'preview' : 'main';
        return `${idTag}_${this.coords.project$.currentId}`;
    }

    export(isStandalone = true) {
        const ls: [string, string[]][] = [[this.id, ['dataset-xitemLs']]];
        if (isStandalone)
            this.coords.export$.export(
                'collection',
                `collection_${this.coords.project$.currentTitle}`,
                ls,
            );
        return ls[0];
    }

    visiblePanelsDialog() {
        this.coords.display$.dialog$.next({
            action: 'dataset-visible-panels',
            isPreview: this.isPreview,
        });
    }

    clear() {
        this.isReady = false;
        this._xitemMap.clear();
        this._xitemMainMap.clear();
        this._keys.clear();
    }

    reportData() {
        return this.listInstance();
    }

    listInstance() {
        const xLs = this.coords.resultList$.activeList$.getValue();

        return {
            filter: this.coords.view$.sortedViewLs.map((v) => ({
                tag: v.title,
                indexLs: xLs
                    .map((x, i) => (v.has(x.id) ? i : undefined))
                    .filter((x) => x !== undefined),
            })),
            ls: xLs.map((x) => x.cite()),
        };
    }

    get size() {
        return this._xitemMainMap.size;
    }

    private _processDuplicates(isReset = false) {
        // console.log('PROCESS DUPLICATES', this._xitemMap.size);
        // console.trace()

        this._xitemMainMap.clear();
        this._sortedMainXitemLs = undefined;

        type tpx = { main: XItem; duplLs: XItem[] };

        const sharedKeysMap = new Map<string, tpx>();

        this._xitemMap.forEach((xitm) => {
            xitm.duplicateLs = [];

            xitm.keys!.forEach((k: string) => {
                const tpxObj: tpx | undefined = sharedKeysMap.get(k);
                if (!tpxObj) {
                    sharedKeysMap.set(k, { main: xitm, duplLs: [] });
                    return;
                }
                tpxObj.duplLs.push(xitm);
            });
        });

        const duplM = new Map<XItem, Set<XItem>>();

        sharedKeysMap.forEach((tpxObj) => {
            tpxObj.duplLs.forEach((xitm) => {
                let s = duplM.get(xitm);
                if (!s) {
                    s = new Set<XItem>();
                    duplM.set(xitm, s);
                }
                s.add(tpxObj.main);
            });
        });

        this._xitemMap.forEach((xitm) => {
            const s = duplM.get(xitm);
            s
                ? s.forEach((mainXitm) => {
                      mainXitm.duplicateLs.push(xitm);
                      xitm.parentXitem = mainXitm;
                  })
                : this._xitemMainMap.set(xitm.id, xitm);
        });

        this._xitemMainMap.forEach((xitm) => {
            const s = xitm.noDuplicateSet;
            if (!s) return;

            Array.from(xitm.duplicateLs).forEach((duplXitm, i) => {
                if (s.has(duplXitm.id)) {
                    xitm.duplicateLs.splice(i, 1);
                    this._xitemMainMap.set(duplXitm.id, duplXitm);
                    duplXitm.parentXitem = undefined;
                }
            });
        });

        this.coords.display$.markForCheck();
        // console.log("%c SIZE", this.coords.consoleEm("red", "yellow"), this.isPreview, this.size)
        // this._informAutotag(Array.from(this._xitemMainMap.values()));
        this.coords.dataset$.refreshPreviewId2mainIdMap();

        if (!this._sortMeta)
            this._sortMeta = {
                type: 'year',
                isDescending: true,
            };

        this.coords.keyword$.updateDataset(this);

        this.sort(this._sortMeta.type, this._sortMeta.isDescending);
        !this.isPreview && this.coords.searchArchive$.clearCacheHitset();

        this.isReady = true;
        if (this.coords.dataset$.isReady) this.coords.script$.onLoad();
        if (!this.isPreview) {
            this.coords.view$.checkCollectionItems();
        }
    }

    docx(ls: any[]) {
        this._xitemMainMap.forEach((xitm) =>
            ls.push({
                type: 'paragraph',
                payload: xitm.docx(),
            }),
        );
    }

    fieldPreview(fieldTag: string, fieldValue: string) {
        // console.log("FIELD PREVIEW", fieldTag, fieldValue)
        const ls: string[] = [];
        this._xitemMainMap.forEach((xitm) => {
            if (xitm.hasValue(fieldTag, fieldValue)) ls.push(xitm.id);
        });
        // const s = new Set(ls);

        const stg = this.coords.stage$.current!.tag;

        this.coords.resultList$.activateSubset({
            idSet: new Set(ls),
            title: fieldValue,
            prefix: this.coords.text(fieldTag),
            icon: 'field',
            class: 'selection-in-general',
            back: () => this.coords.stage$.set(stg),
            stage: stg,
        });
    }

    private _manifestStore() {
        const u = this.coords.user$.current;
        if (!this.isPreview) {
            const ls = Array.from(this._xitemMap.values())
                .filter((x) => !u || u.id === x.ownerId)
                .map((xitm) => xitm.save());
            if (u) u.collectionDataLs = ls;
            this.coords.manifest$.store({
                type: 'dataset-xitemLs',
                id: this.id,
                valueMap: new Map<string, any[]>([['xitemLs', ls]]),
            });
        }
    }

    importXitemLs(xitemLs: XItem[], isLoad = false) {
        if (!xitemLs) return;
        const finalLs: XItem[] = [];

        xitemLs.forEach((origXitm) => {
            if (!origXitm) return;
            const xitm = origXitm.copy();

            if (!this.isPreview) {
                const dcmId = xitm.shortIDdecimal;
                if (dcmId) {
                    if (dcmId > this.shortIdCounter) this.shortIdCounter = dcmId + 1;
                } else xitm.shortIDdecimal = ++this.shortIdCounter;

                if (!isLoad) xitm.id = this.coords.uniqueId();
            }
            if (!xitm.id) xitm.id = this.coords.uniqueId();
            this._xitemMap.set(xitm.id, xitm);
            xitm.assignDataset(this);
            finalLs.push(xitm);
        });
        this.requestReduplicate();
        if (!isLoad) this.markForManifestStore();
        this.coords.display$.markForCheck();
        return finalLs;
    }

    addXItem(xitm: XItem) {
        xitm.ownerId = this.coords.user$.currentId;
        const ls = this.importXitemLs([xitm]);
        return ls ? ls[0] : undefined;
    }

    deleteXitemIdLs(idLs: string[]) {
        const allLs: string[] = [];
        idLs.forEach((xitmId) => {
            const xitm = this._xitemMap.get(xitmId);
            if (!xitm) return;
            allLs.push(xitm.id);
            xitm.duplicateLs.forEach((x) => allLs.push(x.id));
        });
        allLs.forEach((x) => this._xitemMap.delete(x));
        this.requestReduplicate();
        this.markForManifestStore();
        this.coords.display$.markForCheck();
    }

    xitemLsFromIdLs(idLs: string[]): XItem[] {
        return idLs.map((x) => this._xitemMap.get(x)!).filter((x) => x);
    }

    get mainXitemLs() {
        return Array.from(this._xitemMainMap.values());
    }

    get idSet() {
        return new Set(this._xitemMainMap.keys());
    }

    get sortedMainXitemLs() {
        if (!this._sortedMainXitemLs) this._sortedMainXitemLs = this.mainXitemLs;
        return this._sortedMainXitemLs;
    }

    setSortedMainXitemLs(ls: XItem[]) {
        this._sortedMainXitemLs = ls;
    }

    get allXitemLs() {
        return Array.from(this._xitemMap.values());
    }

    get(xitmId: string): XItem | undefined {
        return this._xitemMainMap.get(xitmId);
    }

    get idIncludingDuplicatesSet(): Set<string> {
        return new Set<string>(this._xitemMap.keys());
    }

    get xitemIdSet() {
        //includes ids' of duplicates
        return new Set<string>(this._xitemMap.keys());
    }

    get mainIdSet() {
        return new Set<string>(this._xitemMainMap.keys());
    }

    get mainIdList() {
        return Array.from(this._xitemMainMap.keys());
    }

    hasXitemId(tag: string) {
        return this._xitemMap.get(tag);
    }

    sortMeta() {
        if (!this._sortMeta) {
            this._sortMeta = { type: 'year', isDescending: true };
        }
        return this._sortMeta;
    }

    async sort(tp: string, isDescending?: boolean) {
        const payload = { type: tp, isDescending: !!isDescending };
        // this.coords.autotag$.setConstant('sort', payload);
        // console.log("%c REQUEST SORT SEND ", this.coords.consoleEm("blue", "yellow"),
        //     payload, "osPreview: ", this.isPreview)

        this._sortMeta = payload;

        // this.coords.autotag$.post('sort', Object.assign({}, payload, meta));
        // console.log('SORT META', this._sortMeta);
        const lastString = '\uFFFF';
        const lastNumber = Number.MIN_SAFE_INTEGER;
        const valueLs = this.mainXitemLs.map((xitm) => {
            let value: string | number;
            let assistValue: string | number;

            switch (this._sortMeta!.type) {
                case 'criterion':
                    value = xitm.keywordSum;
                    break;
                case 'year':
                    value = parseInt(xitm.get('year')) || lastNumber;
                    break;
                case 'title':
                    value = (xitm.title || lastString).toLowerCase();
                    break;
                case 'source':
                    value = (xitm.get('source') || lastString).toLowerCase();
                    assistValue = parseInt(xitm.get('year')) || lastNumber;
                    break;
                case 'first-author':
                    value = ((xitm.get('authorLs') || [])[0] || lastString).toLowerCase();
                    break;
                case 'last-author':
                    const authLs = xitm.get('authorLs') || [];
                    if (!authLs.length) value = lastString;
                    else value = authLs[authLs.length - 1].toLowerCase();
                    break;

                case 'random':
                    value = Math.random();
                    break;
            }

            if (!assistValue!) assistValue = (xitm.get('source') || lastString).toLowerCase();

            return { id: xitm.id, value: value!, assistValue: assistValue };
        });

        const resp: any = await this.coords.worker$.autotag.call('sort', {
            itemLs: valueLs,
            isDescending,
        });

        this._sortedMainXitemLs = resp.itemLs.map((id: string, i: number) => {
            const xitm = this._xitemMap.get(id)!;
            xitm.order = i + 1;
            return xitm;
        });

        this.coords.resultList$.markForCheck();
    }

    async runSearchProcedure(inputDct?: any) {
        if (!inputDct) inputDct = await this.coords.filesystem$.pickJsonFile();
        console.log('RUN SEARCH PROCEDURE', inputDct);
        const inpMap = new Map<string, any>(Object.entries(inputDct.data));
        inpMap.forEach((ls, k) =>
            inpMap.set(
                k,
                ls.map((x: string) => new RegExp(x, 'i')),
            ),
        );
        console.log('INPUT MAP', inpMap);
        const ls: string[] = [];

        let xitemLs = Array.from(this._xitemMainMap.values());

        const activeProcedure = inputDct.activeProcedure;
        const stepLs = inputDct.procedures[activeProcedure];

        stepLs.forEach((step: any) => {
            console.log('STEP', step);
            const alwaysRgLs = step.always && inpMap.get(step.always);
            const excludeRgLs = step.exclude && inpMap.get(step.exclude);
            const selectionRgLs = step.selection && inpMap.get(step.selection);
            const newXitemLs: XItem[] = [];

            xitemLs.map((xitm) => {
                const isAlways =
                    alwaysRgLs &&
                    alwaysRgLs.some(
                        (rgx: RegExp) => rgx.test(xitm.title) || rgx.test(xitm.abstract),
                    );

                if (
                    !isAlways &&
                    excludeRgLs &&
                    excludeRgLs.some(
                        (rgx: RegExp) => rgx.test(xitm.title) || rgx.test(xitm.abstract),
                    )
                )
                    return;
                if (selectionRgLs) {
                    if (
                        selectionRgLs.some(
                            (rgx: RegExp) => rgx.test(xitm.title) || rgx.test(xitm.abstract),
                        )
                    )
                        newXitemLs.push(xitm);
                } else newXitemLs.push(xitm);
            });
            xitemLs = newXitemLs;
        });

        return {
            title: activeProcedure || 'Search procedure',
            resultIdLs: xitemLs.map((xitm) => xitm.id),
        };
    }

    get scriptClass() {
        return new ScriptDataset(this.coords, this);
    }

    get title() {
        return this.coords.text(this.isPreview ? 'preview' : 'collection');
    }
}
